import {
    Box,
    Card,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { RelatedPerson } from '@/types';
import { fetchProjectRelatedPeople } from '@/utils/related_people_utils';

type RelatedPeopleTableProps = {
    projectId: number;
    title?: string;
};

export const RelatedPeopleTable = ({
    projectId,
    title = 'Related people',
}: RelatedPeopleTableProps) => {
    const [people, setPeople] = useState<RelatedPerson[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        fetchProjectRelatedPeople(projectId)
            .then(data => {
                if (!cancelled) setPeople(data);
            })
            .catch(e => {
                if (!cancelled) {
                    setError(e instanceof Error ? e.message : 'Failed to load related people');
                    setPeople([]);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [projectId]);

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ px: 1, mb: 1 }}>
                {title}
            </Typography>
            <Card>
                {loading ? (
                    <Box display="flex" justifyContent="center" py={3}>
                        <CircularProgress size={24} />
                    </Box>
                ) : error ? (
                    <Typography color="error" sx={{ p: 2 }}>
                        {error}
                    </Typography>
                ) : people.length === 0 ? (
                    <Typography color="text.secondary" sx={{ p: 2 }}>
                        No related people linked to this project.
                    </Typography>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Email</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {people.map(person => (
                                    <TableRow key={person.id}>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {person.name}
                                        </TableCell>
                                        <TableCell sx={{ wordBreak: 'break-word' }}>
                                            {person.email}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Card>
        </Box>
    );
};
